import {FC} from 'react';
import DOMPurify from 'dompurify';
import Help from '../ui/Help/Help';
import i18n from './i18n';

/**
 * Open external links in a new tab. DOMPurify drops the `target` attribute by
 * default, but help text routinely links to vendor docs (import connectors, the
 * ABA, etc.) that should not navigate the user away from an in-progress task.
 */
const newTabHook = (node: Element) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
    }
};

/**
 * Sanitize a help (h_) HTML string for rendering. Same DOMPurify pass the invoice
 * letterhead uses (createMarkup), plus the new-tab hook above. The hook is added and
 * removed around this one synchronous call so it never affects other DOMPurify
 * consumers such as the letterhead.
 *
 * Exported so a characterization test can snapshot the exact production render of
 * every i18n help string.
 */
export const createHelpMarkup = (value: string): {__html: string} => {
    DOMPurify.addHook('afterSanitizeAttributes', newTabHook);
    try {
        return {__html: DOMPurify.sanitize(value)};
    }
    finally {
        DOMPurify.removeHook('afterSanitizeAttributes');
    }
};

export type HelpMessageProps = {
    // The module in i18n to use
    module: string,

    // The name of the field in the module with the help info
    field: string,

    // Params to pass to the i18n module
    params?: any,

    // Should the text be bolded? (true by default)
    bold?: boolean,

    // Should the label be hidden?
    hideLabel?: boolean,

    // Switch background and foreground colors
    invertColors?: boolean,

    // Always show this help icon, even if Help is turned off
    showOverride?: boolean,
}

const HelpMessage: FC<HelpMessageProps> = props => {
    // Get the uiLabel
    const uiLabel = i18n.l(props.module, props.field, props.params);

    // Get the description
    const rawMessage = i18n.d(props.module, props.field, props.params);

    // Help text (h_) in the i18n modules is authored as translatable HTML strings
    // (a small p/ul/li/b/a/br subset). Render those through createHelpMarkup — the
    // DOMPurify-sanitized path used for the invoice letterhead, plus new-tab handling
    // for external links — so the markup shows as formatted HTML rather than escaped
    // text. Legacy entries that are still JSX (a ReactElement) render as-is until they
    // are converted to strings (OWA-478).
    const message = typeof rawMessage === 'string'
        ? <div dangerouslySetInnerHTML={createHelpMarkup(rawMessage)}/>
        : rawMessage;

    const titleAsString = uiLabel instanceof String ? undefined : props.field;

    return <div className='help__label'>
        {!props.hideLabel && props.bold && <div className="help__label-text">{uiLabel}</div>}
        {!props.hideLabel && !props.bold &&
         <div className="help__label-text" style={{fontWeight: 'normal', cursor: 'pointer'}}>{uiLabel}</div>
        }
        <Help title={uiLabel}
              message={message}
              titleAsString={titleAsString}
              showOverride={props.showOverride}
              invertColors={props.invertColors}
        />
    </div>;
};

export default HelpMessage;

